import { useEffect, useCallback, useState } from "react";

import {
  useDocumentMetadata,
  useDocumentUrl,
  useInvalidateDocumentUrl,
  useDocumentExtractionPoller,
} from "@/shared/hooks/useDocumentMetadata";
import { buildUserDocument } from "@/shared/utils/documentUtils";
import { Callout } from "@/shared/components/ui/callout";
import { DocumentPreview } from "./DocumentPreview";
import { CitationProvider } from "./CitationContext";
import { format } from "date-fns";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import { X } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import DocumentSummaryAndInsights from "./DocumentSummaryAndInsights";
import { IconButton } from "@/shared/components/ui/icon-button";

type MobileTab = "preview" | "insights";

export default function DocumentSheet({
  isOpen,
  setIsOpen,
  documentId,
  documentName,
  documentCreatedAt,
  estateId,
  onDocumentLoaded,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  documentId: number;
  documentName: string;
  documentCreatedAt: Date;
  estateId?: number;
  onDocumentLoaded?: () => void;
}) {
  const {
    metadata,
    isLoading: metadataLoading,
    error: metadataError,
  } = useDocumentMetadata({ documentId, estateId });

  const {
    previewUrl,
    isLoading: urlLoading,
    error: urlError,
  } = useDocumentUrl({ documentId, estateId });

  const invalidateDocumentUrl = useInvalidateDocumentUrl({
    documentId,
    estateId,
  });

  useDocumentExtractionPoller({
    documentId,
    extractionStatus: metadata?.extractionStatus,
    smartSummaryStatus: metadata?.smartSummary?.status,
    hasSpecificDocData: !!metadata?.specificDocExtraction,
  });

  const handleRefreshUrl = useCallback(async () => {
    await invalidateDocumentUrl();
  }, [invalidateDocumentUrl]);

  useEffect(() => {
    if (!metadataLoading && !urlLoading && metadata && previewUrl) {
      onDocumentLoaded?.();
    }
  }, [metadataLoading, urlLoading, metadata, previewUrl, onDocumentLoaded]);

  const [mobileTab, setMobileTab] = useState<MobileTab>("preview");

  const error = metadataError || urlError;
  if (error) {
    return <Callout variant="error">Failed to load document</Callout>;
  }

  const previewDocument = metadata && previewUrl ? buildUserDocument(metadata, previewUrl) : null;
  const insightsDocument = metadata ? buildUserDocument(metadata) : null;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent className="w-full max-w-none sm:max-w-none p-0 h-screen" showClose={false}>
        <SheetHeader className="flex flex-row items-center justify-between border-b border-primary bg-subtle p-4 sm:p-6 gap-2">
          <div className="min-w-0 flex-1 pr-2 sm:pr-4">
            <SheetTitle className="truncate text-left max-w-[75vw] sm:max-w-md md:max-w-full leading-tight font-sans font-semibold">
              {metadata?.displayName || documentName}
            </SheetTitle>
            <SheetDescription className="text-left text-sm text-stone-500 leading-snug">
              {`Uploaded on ${format(documentCreatedAt, "PP")}`}
            </SheetDescription>
          </div>
          <SheetClose asChild>
            <IconButton className="shrink-0 self-center" label="Close" tooltip={false}>
              <X />
            </IconButton>
          </SheetClose>
        </SheetHeader>
        <CitationProvider>
          <div
            className="flex md:hidden shadow-[inset_0_-1px_0_0_#e7e5e4]"
            role="tablist"
            aria-label="Document view"
          >
            <button
              type="button"
              role="tab"
              id="tab-preview"
              aria-controls="panel-preview"
              aria-selected={mobileTab === "preview"}
              onClick={() => setMobileTab("preview")}
              className={cn(
                "flex-1 py-2.5 text-sm font-medium text-center transition-colors border-b-2",
                mobileTab === "preview"
                  ? "border-stone-900 text-primary"
                  : "border-transparent text-secondary",
              )}
            >
              Preview
            </button>
            <button
              type="button"
              role="tab"
              id="tab-insights"
              aria-controls="panel-insights"
              aria-selected={mobileTab === "insights"}
              onClick={() => setMobileTab("insights")}
              className={cn(
                "flex-1 py-2.5 text-sm font-medium text-center transition-colors border-b-2",
                mobileTab === "insights"
                  ? "border-stone-900 text-primary"
                  : "border-transparent text-secondary",
              )}
            >
              Insights
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[60%_40%] h-[calc(100vh-100px)] overflow-hidden">
            <div
              role="tabpanel"
              id="panel-preview"
              aria-labelledby="tab-preview"
              className={cn(
                "preview md:border-r border-stone-200 overflow-hidden",
                mobileTab !== "preview" ? "hidden md:block" : "",
              )}
            >
              {previewDocument ? (
                <DocumentPreview
                  document={previewDocument}
                  onDocumentDeleted={() => setIsOpen(false)}
                  onRefreshUrl={handleRefreshUrl}
                />
              ) : (
                <div className="flex items-center justify-center h-full bg-stone-50">
                  <div className="text-stone-500">Loading document...</div>
                </div>
              )}
            </div>
            <div
              role="tabpanel"
              id="panel-insights"
              aria-labelledby="tab-insights"
              className={cn(
                "details bg-white overflow-hidden",
                mobileTab !== "insights" ? "hidden md:block" : "",
              )}
            >
              {insightsDocument ? (
                <DocumentSummaryAndInsights document={insightsDocument} />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-stone-500">Loading...</div>
                </div>
              )}
            </div>
          </div>
        </CitationProvider>
      </SheetContent>
    </Sheet>
  );
}
